import React, { type ReactNode, useState, useEffect } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import InlineEdit from '@atlaskit/inline-edit';
import Select, { type ValueType, type SelectProps } from '@atlaskit/select';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import messages from '../../messages';
import { InlineEditContainer } from '../../styled';

export type SelectFieldViewProps<TOption, TMulti extends boolean = false> = {
	isMulti?: TMulti;
	label: string;
	value: ValueType<TOption, TMulti>;
	valueAsString: string | null | undefined;
	onSelection: (selection: ValueType<TOption, TMulti>, analyticsEvent: UIAnalyticsEvent) => void;
	readView: () => ReactNode;
} & Omit<SelectProps<TOption, TMulti>, 'isMulti' | 'value'>;

export function SelectFieldView<TOption, TMulti extends boolean = false>({
	id,
	value,
	valueAsString,
	label,
	onSelection,
	readView,
	closeMenuOnSelect = true,
	...etc
}: SelectFieldViewProps<TOption, TMulti>) {
	const { formatMessage } = useIntl();
	const [isEditing, setIsEditing] = useState(false);
	const [currentValue, setCurrentValue] = useState(value);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const valueForAriaLabel = valueAsString || formatMessage(messages.noValue);

	const onSelectOptionWithAnalytics = (selectedValue: ValueType<TOption, TMulti>) => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'selectOption',
		});
		onSelection(selectedValue, analyticsEvent);
	};

	useEffect(() => {
		setCurrentValue(value);
	}, [value]);

	return (
		<InlineEditContainer>
			<InlineEdit<ValueType<TOption, TMulti>>
				isEditing={isEditing}
				defaultValue={undefined}
				editButtonLabel={formatMessage(messages.editButtonLabel, {
					label,
					value: valueForAriaLabel,
				})}
				hideActionButtons
				onEdit={() => {
					setIsEditing(true);
				}}
				editView={(fieldProps) => (
					<Select<TOption, TMulti>
						{...fieldProps}
						{...etc}
						inputId={id}
						value={currentValue}
						closeMenuOnSelect={closeMenuOnSelect}
						onChange={(newValue: ValueType<TOption, TMulti>) => {
							setCurrentValue(newValue);

							if (closeMenuOnSelect) {
								onSelectOptionWithAnalytics(newValue);
							}
						}}
						spacing="compact"
						isClearable
						autoFocus
						openMenuOnFocus
						onMenuClose={() => {
							if (!closeMenuOnSelect) {
								onSelectOptionWithAnalytics(currentValue);
							}

							setIsEditing(false);
						}}
					/>
				)}
				// handled by select
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				onConfirm={() => {}}
				readViewFitContainerWidth
				readView={readView}
			/>
		</InlineEditContainer>
	);
}
